import { Button } from '@base-ui/react';
import styled from '@emotion/styled';

import { DISABLED_EXCLUSIONS, DISABLED_SELECTORS, focusRing } from '../../interaction-states';
import { forHover } from '../../responsive';
import { typographyVariantStyles } from '../../typography-variants';
import type { PaginationSize } from './types';

const PAGE_BUTTON_TRANSIENT_PROPS = new Set(['$active', '$paginationSize']);
const ELLIPSIS_TRANSIENT_PROPS = new Set(['$size']);

const SIZE_CONFIG: Record<PaginationSize, { boxSize: number; radius: number }> = {
  md: { boxSize: 40, radius: 12 },
  sm: { boxSize: 32, radius: 8 },
};

const pageButtonTypography = (size: PaginationSize) => ({
  ...(size === 'md' ? typographyVariantStyles.body2 : typographyVariantStyles.body3),
  fontWeight: 500,
});

export const StyledPaginationRoot = styled('ul')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  listStyle: 'none',
  padding: 0,
  margin: 0,
});

export const StyledPageButton = styled(Button, {
  shouldForwardProp: (prop) => !PAGE_BUTTON_TRANSIENT_PROPS.has(prop as string),
})<{ $active: boolean; $paginationSize: PaginationSize }>(({ theme, $active, $paginationSize }) => {
  const { boxSize, radius } = SIZE_CONFIG[$paginationSize];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: boxSize,
    height: boxSize,
    borderRadius: radius,
    cursor: 'pointer',
    outline: 'none',
    border: 'none',
    backgroundColor: $active ? theme.colors.brand.solid : 'transparent',
    boxSizing: 'border-box',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
    ...pageButtonTypography($paginationSize),
    color: theme.colors.text.primary,

    transition: theme.motion.hover,

    [forHover]: {
      [`&:hover${DISABLED_EXCLUSIONS}`]: {
        backgroundColor: $active ? theme.colors.brand.solid : theme.colors.brand.hover,
      },
    },

    [DISABLED_SELECTORS]: {
      color: theme.colors.text.disabled,
      cursor: 'not-allowed',
    },

    '&:focus-visible': {
      backgroundColor: $active ? theme.colors.brand.solid : theme.colors.black[4],
    },

    ...focusRing(theme),
  };
});

export const StyledEllipsis = styled('span', {
  shouldForwardProp: (prop) => !ELLIPSIS_TRANSIENT_PROPS.has(prop as string),
})<{ disabled?: boolean; $size: PaginationSize }>(({ theme, disabled, $size }) => {
  const { boxSize, radius } = SIZE_CONFIG[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: boxSize,
    height: boxSize,
    borderRadius: radius,
    boxSizing: 'border-box',
    flexShrink: 0,
    color: theme.colors.white[100],
    pointerEvents: 'none',
    opacity: disabled ? 0.5 : 1,
    ...pageButtonTypography($size),
  };
});
